import { Translator } from 'Translator';
import Cookies from 'js-cookie';
import contentUrlMapper from 'modules/experiences/utils/contentUrlMapper';
import Prismic from 'prismic-javascript';
import {
  CLASSICS_DOCUMENT_TYPE,
  COLLECTION_DOCUMENT_TYPE,
  HOST_TOOLS_DOCUMENT_TYPE,
  INFORMATION_PAGE_DOCUMENT_TYPE,
  LEARNING_CENTER_DOCUMENT_TYPE,
  LIVE_DOCUMENT_TYPE,
  LOCATION_PAGE_DOCUMENT_TYPE,
  LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE,
  PARTNER_CENTER_DOCUMENT_TYPE,
  PRISMIC_API_ENDPOINT,
  PRISMIC_API_ENDPOINT_V2,
  PRISMIC_API_ENDPOINT_V2_TOKEN,
  WL_CONNECT_DOCUMENT_TYPE,
} from 'utils/Prismic/PrismicConstants';
import PrismicResponseParser from 'utils/Prismic/PrismicResponseParser';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';

export const prismicUidToPath = (uid) => uid.replace(new RegExp('_', 'g'), '/');

const pathPrefixes = ['/experiences/', '/locations/', '/locals/', '/info/'];
export const pathToPrismicUid = (
  pathToCheck: string,
  splitOn: string[] = pathPrefixes,
) => {
  const path = contentUrlMapper.map(pathToCheck);
  let result = '';
  splitOn.forEach((prefix) => {
    if (!path.includes(prefix) || result.length > 0) {
      return;
    }
    result = path
      .split(prefix)[1]
      .split('/')
      .filter((part) => part !== '')
      .join('_');
  });
  return result;
};

export const linkResolver = (doc) => {
  // Pretty URLs for known types
  if (doc.type === LOCATION_PAGE_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/prismic/${prismicUidToPath(doc.uid)}/`,
      Translator.getLanguageCode(),
    );
  }
  if (doc.type === INFORMATION_PAGE_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/info/${prismicUidToPath(doc.uid)}/`,
      Translator.getLanguageCode(),
    );
  }
  if (
    doc.type === LEARNING_CENTER_DOCUMENT_TYPE ||
    doc.type === PARTNER_CENTER_DOCUMENT_TYPE ||
    doc.type === CLASSICS_DOCUMENT_TYPE ||
    doc.type === LIVE_DOCUMENT_TYPE ||
    doc.type === WL_CONNECT_DOCUMENT_TYPE ||
    doc.type === HOST_TOOLS_DOCUMENT_TYPE
  ) {
    return generatePathnameWithLangCode(
      `/learning-center/preview-article/${doc.type}/${prismicUidToPath(
        doc.uid,
      )}/`,
      Translator.getLanguageCode(),
    );
  }
  if (doc.type === COLLECTION_DOCUMENT_TYPE) {
    return generatePathnameWithLangCode(
      `/learning-center/preview-collection/${prismicUidToPath(doc.uid)}/`,
      Translator.getLanguageCode(),
    );
  }

  // Fallback for other types, in case new custom types get created
  return '/doc/' + doc.id;
};

class PrismicClient {
  static query = (query: any) => {
    return Prismic.getApi(PRISMIC_API_ENDPOINT, {
      accessToken: PRISMIC_API_TOKEN,
    })
      .then((api) => {
        return api.query(query, null);
      })
      .then((resp) => {
        return resp.results[0];
      });
  };

  static getInformationPage = (uid: string, lang: string) => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        return client.getByUID(INFORMATION_PAGE_DOCUMENT_TYPE, uid, {
          lang: langParam,
          ref: token,
        });
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getInformationPage(uid, 'en');
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getCollection = (uid: string, lang: string) => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        return client.getByUID(COLLECTION_DOCUMENT_TYPE, uid, {
          lang: langParam,
          ref: token,
        });
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getInformationPage(uid, 'en');
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getLearningCenterArticles = (
    category: string,
    lang: string,
    docType: string,
  ) => {
    // const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((api) => {
        return api.query(
          [
            Prismic.Predicates.at('document.type', docType),
            Prismic.Predicates.at(`my.${docType}.category`, category),
          ],
          { orderings: `[my.${docType}.order, my.${docType}.step]` },
        );
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getLearningCenterArticles(
            category,
            'en',
            docType,
          );
        }
        return response;
      })
      .catch((e) => {
        console.error(
          `Error in getLearningCenterArticles for ${category} and ${lang}`,
          e,
        );
        return null;
      });
  };

  static getTravelSafelyArticles = (
    category: string,
    lang: string,
    docType: string,
  ) => {
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((api) => {
        return api.query(
          [
            Prismic.Predicates.at('document.type', docType),
            Prismic.Predicates.at(`my.${docType}.category`, category),
          ],
          { orderings: `[my.${docType}.order, my.${docType}.step]` },
        );
      })
      .catch((e) => {
        console.error(
          `Error in ge getTravelSafelyArticles ${category} and ${lang}`,
          e,
        );
        return null;
      });
  };

  static getArticleByUid = (uid: string, lang: string, docType: string) => {
    const langParam = lang === 'en' ? 'en-us' : `${lang}-${lang}`;
    return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
      accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
    })
      .then((client) => {
        const token = Cookies.get('Prismic-token');
        return client.getByUID(docType, uid, { lang: langParam, ref: token });
      })
      .then((response) => {
        if (!response && lang !== 'en') {
          return PrismicClient.getArticleByUid(uid, 'en', docType);
        }
        return response;
      })
      .catch((e) => {
        console.error(`Error in getInformationPage for ${uid} and ${lang}`, e);
        return null;
      });
  };

  static getLocationPageInterlinkDocument = (uid: string) => {
    return PrismicClient.query([
      Prismic.Predicates.at(
        'document.type',
        LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE,
      ),
      Prismic.Predicates.at(
        `my.${LOCATION_PAGE_INTERLINKING_DOCUMENT_TYPE}.uid`,
        uid,
      ),
    ]);
  };

  static getLocationPageDocument = (uid: string) => {
    return PrismicClient.query([
      Prismic.Predicates.at('document.type', LOCATION_PAGE_DOCUMENT_TYPE),
      Prismic.Predicates.at(`my.${LOCATION_PAGE_DOCUMENT_TYPE}.uid`, uid),
    ]).then((results) => {
      if (!results || results.uid !== uid) {
        return {
          documentId: undefined,
          topThingsTitle: '',
          topThingsDescription: '',
          topThingsToDo: [],
          articles: [],
          interlinking: [],
        };
      }
      const documentId = results.id;
      const topThingsToDoProm = Promise.resolve(
        results.data.topitems
          .filter((slice) => slice.slice_type === 'banner')
          .map((slice) => PrismicResponseParser.parseBannerSlice(slice)),
      );
      const articlesProm = Promise.resolve(
        results.data.topitems
          .filter((slice) => slice.slice_type === 'article')
          .map((slice) => PrismicResponseParser.parseArticleSlice(slice)),
      );
      const topThingsTitle = results?.data?.title?.[0]?.text || '';
      const topThingsDescription = results?.data?.subtitle?.[0]?.text || '';
      const interlinkingProm = results.data.topitems
        .filter((topItem) => topItem.slice_type === 'interlink')
        .map((interlink) =>
          Promise.all(
            Object.keys(interlink.value[0])
              .map((tab) => {
                const link = interlink?.value?.[0]?.[tab] || null;
                if (!link || link.link_type !== 'Document') {
                  return null;
                }
                return PrismicClient.getLocationPageInterlinkDocument(link.uid);
              })
              .filter((val) => val !== null),
          ).then((interlinkingDocuments) =>
            interlinkingDocuments.map((doc) =>
              PrismicResponseParser.parseInterlinkingDocument(doc),
            ),
          ),
        )[0];

      return Promise.all([
        topThingsToDoProm,
        articlesProm,
        interlinkingProm,
      ]).then(([topThingsToDo, articles, interlinking]) => {
        return {
          documentId,
          topThingsTitle,
          topThingsDescription,
          topThingsToDo,
          articles,
          interlinking,
        };
      });
    });
  };

  static initPreview = (params: URLSearchParams) => {
    const token = params.get('token');
    if (token && token.indexOf('withlocalscontent')) {
      Cookies.set('Prismic-token', token);
      return Prismic.getApi(PRISMIC_API_ENDPOINT_V2, {
        accessToken: PRISMIC_API_ENDPOINT_V2_TOKEN,
      }).then((api) => api.previewSession(token, linkResolver, '/'));
    }
    return Prismic.getApi(PRISMIC_API_ENDPOINT, {
      accessToken: PRISMIC_API_TOKEN,
    }).then((api) => api.previewSession(token, linkResolver, '/'));
  };
}

export default PrismicClient;
